<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { v4 as uuidv4 } from 'uuid';
  import { ClubService } from '@/api/services/club.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiListbox, {
    IPosUiListboxItem,
  } from '@/components/pos-v2/ui/PosUiListbox.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { getClubCurrencyDefaults } from '@/domain/club/club-currency-defaults';
  import { AlertType } from '@/interfaces/IAlert';
  import { IClubMember } from '@/interfaces/IClubMember';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const MODAL_NAME = 'v2-club-adjust';

  const props = defineProps<{
    member: IClubMember;
    mode: 'points' | 'cash';
  }>();

  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const symbol = computed(() => restaurantStore.currency?.symbol ?? '$');
  const defaults = getClubCurrencyDefaults(
    restaurantStore.currency?.value ?? null
  );
  const isPoints = computed(() => props.mode === 'points');

  const direction = ref<'add' | 'subtract'>('add');
  const amountRaw = ref('');
  const bucket = ref<'principal' | 'bonus'>('principal');
  const reason = ref('');
  const saving = ref(false);

  // One key per modal open: retrying a failed request replays instead of
  // double-applying.
  const idempotencyKey = uuidv4();

  const bucketItems = computed<IPosUiListboxItem[]>(() => [
    { id: 'principal', label: i18nT('club.adjust.bucketPrincipal') },
    { id: 'bonus', label: i18nT('club.adjust.bucketBonus') },
  ]);

  const amount = computed(() => {
    const num = Number(amountRaw.value);
    return isNaN(num) ? 0 : num;
  });

  const resultingBalance = computed(() => {
    const sign = direction.value === 'add' ? 1 : -1;
    if (isPoints.value) return props.member.pointsBalance + sign * amount.value;
    return (
      Math.round((props.member.cashTotal + sign * amount.value) * 100) / 100
    );
  });

  async function save() {
    if (saving.value) return;

    if (!(amount.value > 0)) {
      ui.showToast(AlertType.Error, i18nT('club.adjust.errorAmount'));
      return;
    }
    if (isPoints.value && !Number.isInteger(amount.value)) {
      ui.showToast(AlertType.Error, i18nT('club.adjust.errorPointsInteger'));
      return;
    }
    if (!reason.value.trim()) {
      ui.showToast(AlertType.Error, i18nT('club.adjust.errorReason'));
      return;
    }

    const signed = direction.value === 'add' ? amount.value : -amount.value;

    try {
      saving.value = true;

      const updated = isPoints.value
        ? await ClubService.adjustPoints(props.member.id, {
            points: signed,
            reason: reason.value.trim(),
            idempotencyKey,
          })
        : await ClubService.adjustCash(props.member.id, {
            amount: signed,
            bucket: bucket.value,
            reason: reason.value.trim(),
            idempotencyKey,
          });

      ui.showToast(AlertType.Success, i18nT('club.adjust.savedToast'));
      proxy.$parent?.$emit('adjusted', updated);
      // The modal unmounts here; don't touch reactive state afterwards.
      modals.hide(MODAL_NAME);
    } catch (e) {
      ui.serverError(e, i18nT('club.adjust.saveError'));
      saving.value = false;
    }
  }

  function cancel() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="v2-modal-club-adjust">
    <div class="modal-header">
      <span class="modal-title">
        {{
          isPoints ? $t('club.adjust.titlePoints') : $t('club.adjust.titleCash')
        }}
      </span>
      <span class="modal-subtitle">{{ member.name }}</span>
    </div>

    <div class="modal-content">
      <div class="direction-control">
        <button
          class="direction-option"
          :class="{ active: direction === 'add' }"
          type="button"
          @click="direction = 'add'"
        >
          {{ $t('club.adjust.add') }}
        </button>
        <button
          class="direction-option"
          :class="{ active: direction === 'subtract' }"
          type="button"
          @click="direction = 'subtract'"
        >
          {{ $t('club.adjust.subtract') }}
        </button>
      </div>

      <div class="adjust-field">
        <label class="adjust-label">
          {{
            isPoints
              ? $t('club.adjust.amountPoints')
              : $t('club.adjust.amountCash', { symbol })
          }}
        </label>
        <PosUiInput
          :value="amountRaw"
          type="number"
          :inputmode="isPoints ? 'numeric' : 'decimal'"
          :prefix="isPoints ? undefined : symbol"
          :suffix="isPoints ? $t('club.wizard.pointsUnit') : undefined"
          :placeholder="isPoints ? '100' : String(defaults.earnUnitAmount)"
          @input="amountRaw = $event"
        />
      </div>

      <div v-if="!isPoints" class="adjust-field">
        <label class="adjust-label">{{ $t('club.adjust.bucket') }}</label>
        <PosUiListbox
          :value="bucket"
          :items="bucketItems"
          @input="bucket = $event"
        />
        <span class="adjust-hint">{{ $t('club.adjust.bucketHint') }}</span>
      </div>

      <div class="adjust-field">
        <label class="adjust-label">{{ $t('club.adjust.reason') }}</label>
        <PosUiTextarea
          :value="reason"
          :rows="2"
          :maxlength="255"
          :placeholder="$t('club.adjust.reasonPlaceholder')"
          @input="reason = $event"
        />
      </div>

      <div v-if="amount > 0" class="adjust-preview">
        {{
          isPoints
            ? $t('club.adjust.previewPoints', {
                balance: resultingBalance.toLocaleString('es-AR'),
              })
            : $t('club.adjust.previewCash', {
                symbol,
                balance: resultingBalance.toLocaleString('es-AR'),
              })
        }}
      </div>
    </div>

    <div class="modal-actions">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="saving"
        @click="cancel"
      >
        {{ $t('cancel') }}
      </PosUiButton>
      <PosUiButton color="primary" :loading="saving" @click="save">
        {{ $t('club.adjust.confirm') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-modal-club-adjust {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-white;

    .modal-header {
      @apply tw-flex tw-flex-none tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;

      .modal-title {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .modal-content {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-5 tw-py-4;

      .direction-control {
        @apply tw-flex tw-self-start tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

        .direction-option {
          @apply tw-min-h-10 tw-rounded-md tw-px-4 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

          &.active {
            @apply tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
          }
        }
      }

      .adjust-field {
        @apply tw-flex tw-flex-col tw-gap-1.5;

        .adjust-label {
          @apply tw-text-sm tw-font-medium tw-text-neutral-700;
        }

        .adjust-hint {
          @apply tw-text-xs tw-text-neutral-400;
        }
      }

      .adjust-preview {
        @apply tw-rounded-lg tw-bg-neutral-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-tabular-nums tw-text-neutral-700;
      }
    }

    .modal-actions {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-5 tw-py-3;
    }
  }
</style>
